import {
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  HStack,
  Icon
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import {
  chooseDataStateType,
  MaintenanceModalDispLabels
} from '../../../molecules/MaintenanceModalDispLabels';
import { MaintenanceFooter } from '../MaintenanceFooter';
import {
  ArrowUpDownIcon,
  TriangleDownIcon,
  TriangleUpIcon
} from '@chakra-ui/icons';
import { QueryPhrasesApi } from '../../../../apis/QueryMaintenance.api';
import { PhrasesSearchCondition } from '../../../../pages/PhrasesMaintenance';
import {
  CategorySendMessageType,
  MaintenanceCompareEnum
} from '../../../constants/MaintenanceConstants';

export interface PhrasesType {
  id: number;
  body: string;
  count: number;
  category: string;
  created_at: string;
  updated_at: string;
}

const TableColumnEnum = {
  id: 'id',
  body: 'body',
  count: 'count',
  category: 'category',
  created_at: 'created_at',
  updated_at: 'updated_at'
};

interface props {
  PhrasesDatas: PhrasesType[] | undefined;
  setMaintenanceDatas: React.Dispatch<
    React.SetStateAction<PhrasesType[] | undefined>
  >;
  setChooseData: (data: chooseDataStateType) => void;
  onOpenBrowse: () => void;
  onOpenEdit: () => void;
  onToggle: () => void;
  Query: PhrasesSearchCondition;
  setQuery: React.Dispatch<React.SetStateAction<PhrasesSearchCondition>>;
  createOrUpdate: boolean;
  searchSwitch: boolean;
  chooseButton: string;
}
const headers = [
  { label: 'ID', key: 'id' },
  { label: 'カテゴリ', key: 'category' },
  { label: '定型文', key: 'body' },
  { label: '利用回数', key: 'count' },
  { label: '登録日時', key: 'created_at' },
  { label: '更新日時', key: 'updated_at' }
];

const GetCsvFileName = () => {
  const date = new Date();
  const hour =
    date.getMonth().toString().length === 1
      ? '0' + (date.getMonth() + 1).toString()
      : date.getMonth().toString();
  const day =
    date.getDate().toString().length === 1
      ? '0' + date.getDate().toString()
      : date.getDate().toString();
  return 'phrases-' + date.getFullYear() + '-' + hour + '-' + day;
};

export const MaintenancePhrasesTable: React.FC<props> = (props) => {
  const {
    PhrasesDatas,
    setMaintenanceDatas,
    setChooseData,
    onOpenBrowse,
    onOpenEdit,
    onToggle,
    Query,
    setQuery,
    createOrUpdate,
    searchSwitch,
    chooseButton
  } = props;
  const [pageNow, setPageNow] = useState<number>(1);
  const [columnSortType, setColumnSortType] = useState<{
    key: string;
    sort: boolean;
  }>({ key: '', sort: false });
  const pageDispMax = 30;
  const pageMax = 6;
  const csvFileName = GetCsvFileName();
  const GetPageCount = (): number => {
    if (PhrasesDatas !== undefined) {
      const divisionCount = Math.floor(PhrasesDatas.length / pageDispMax);
      const remCount = PhrasesDatas.length % pageDispMax === 0 ? 0 : 1;
      return divisionCount + remCount;
    }
    return 1;
  };
  const ColumnNameTriangleType = (column: string) => {
    if (columnSortType.key === column) {
      if (columnSortType.sort) {
        return TriangleUpIcon;
      } else {
        return TriangleDownIcon;
      }
    }
    return ArrowUpDownIcon;
  };

  const ColumnSortApi = async (column: string) => {
    const params = new URLSearchParams();
    const sortType = !columnSortType.sort;
    params.append('order', column + (sortType ? '_asc' : '_desc'));

    if (Query.count.countData !== '') {
      switch (Query.count.compare) {
        case MaintenanceCompareEnum.Equal:
          params.append('count_eq', Query.count.countData);
          break;
        case MaintenanceCompareEnum.Greater:
          params.append('count_gt', Query.count.countData);
          break;
        case MaintenanceCompareEnum.Less:
          params.append('count_lt', Query.count.countData);
          break;
      }
    }

    Query.category !== '' && params.append('category_id_eq', Query.category);
    Query.phrases !== '' && params.append('body_contains', Query.phrases);
    Query.type !== CategorySendMessageType.None &&
      params.append('type', Query.type);
    Query.dateCreatedFrom !== '' &&
      params.append('created_at_gte', Query.dateCreatedFrom);
    Query.dateCreatedTo !== '' &&
      params.append('created_at_lt', Query.dateCreatedTo);
    Query.dateUpdatedFrom !== '' &&
      params.append('updated_at_gte', Query.dateUpdatedFrom);
    Query.dateUpdatedTo !== '' &&
      params.append('updated_at_lt', Query.dateUpdatedTo);
    const apiQuery = '/?' + params.toString();

    setQuery((pre) => ({ ...pre, sort: { key: column, sortType: sortType } }));
    const response = await QueryPhrasesApi(apiQuery);
    if (response.success && response.data !== null) {
      setMaintenanceDatas(response.data as PhrasesType[]);
      setColumnSortType({ key: column, sort: sortType });
    } else {
      alert(column + 'のソートに失敗しました。');
    }
  };

  useEffect(() => {
    setPageNow(1);
  }, [searchSwitch, chooseButton]);

  useEffect(() => {
    setPageNow(1);
    setColumnSortType({ key: '', sort: false });
  }, [createOrUpdate]);

  return (
    <>
      <TableContainer
        position='absolute'
        h='100%'
        w='100%'
        ml={5}
        overflowY='auto'
        gridRow='2/3'
        gridColumn='1/2'
        overflowX='unset'
      >
        <Table variant='simple'>
          <Thead borderWidth='1px' position='sticky' top={0} zIndex='docked'>
            <Tr bg='gray.800'>
              <Th
                cursor='pointer'
                textAlign='center'
                borderWidth='1px'
                color='white'
                onClick={() => ColumnSortApi(TableColumnEnum.id)}
              >
                <HStack>
                  <label style={{ cursor: 'pointer' }}>ID</label>
                  <Icon
                    ml={1}
                    as={ColumnNameTriangleType(TableColumnEnum.id)}
                  />
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'>
                <HStack>
                  <label>カテゴリ</label>
                </HStack>
              </Th>
              <Th
                cursor='pointer'
                textAlign='center'
                borderWidth='1px'
                color='white'
                onClick={() => ColumnSortApi(TableColumnEnum.body)}
              >
                <HStack>
                  <label style={{ cursor: 'pointer' }}>定型文</label>
                  <Icon
                    ml={1}
                    as={ColumnNameTriangleType(TableColumnEnum.body)}
                  />
                </HStack>
              </Th>
              <Th
                cursor='pointer'
                textAlign='center'
                borderWidth='1px'
                color='white'
                onClick={() => ColumnSortApi(TableColumnEnum.count)}
              >
                <HStack>
                  <label style={{ cursor: 'pointer' }}>利用回数</label>
                  <Icon
                    ml={1}
                    as={ColumnNameTriangleType(TableColumnEnum.count)}
                  />
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'>
                <HStack>
                  <label>登録日時</label>
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'>
                <HStack>
                  <label>更新日時</label>
                </HStack>
              </Th>
              <Th textAlign='center' borderWidth='1px' color='white'></Th>
            </Tr>
          </Thead>
          <Tbody>
            {PhrasesDatas !== undefined &&
              PhrasesDatas.map((data, i) => {
                if (
                  pageDispMax * pageNow - pageDispMax <= i &&
                  i < pageDispMax * pageNow
                ) {
                  return (
                    <Tr key={`${'phrases_' + i}`}>
                      <Td>{data.id}</Td>
                      <Td>{data.category}</Td>
                      <Td>{data.body}</Td>
                      <Td>{data.count}</Td>
                      <Td>{data.created_at}</Td>
                      <Td>{data.updated_at}</Td>
                      <Td>
                        <MaintenanceModalDispLabels
                          data={{ type: 'phrases', phrases: data }}
                          setChooseData={setChooseData}
                          onOpenBrowse={onOpenBrowse}
                          onOpenEdit={onOpenEdit}
                          onToggle={onToggle}
                        />
                      </Td>
                    </Tr>
                  );
                }
              })}
          </Tbody>
        </Table>
      </TableContainer>
      <MaintenanceFooter
        CsvData={PhrasesDatas !== undefined ? PhrasesDatas : []}
        DataLength={PhrasesDatas?.length}
        Headers={headers}
        CsvFileName={csvFileName}
        GetPageCount={GetPageCount}
        pageMax={pageMax}
        pageNow={pageNow}
        setPageNow={setPageNow}
      />
    </>
  );
};
